import React, { createContext, useContext, useState, useEffect } from 'react';
import { volassisAPI } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const userId = localStorage.getItem('userId');
    if (userId) {
      checkAuthStatus(parseInt(userId));
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async (userId) => {
    try {
      const response = await volassisAPI.isAdmin(userId);
      if (response && response.output !== undefined) {
        setIsAdmin(response.output);
        setIsAuthenticated(true);
        setCurrentUser({ id: userId });
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('Attempting login for:', email);
      
      const response = await volassisAPI.signIn(email, password);
      console.log('Login response:', response);
      
      if (response && response.output) {
        // Get user ID from email
        const userIdResponse = await volassisAPI.getId(email);
        console.log('User ID response:', userIdResponse);
        
        if (userIdResponse && userIdResponse.id !== -1) {
          // Check if user is admin
          const adminResponse = await volassisAPI.isAdmin(userIdResponse.id);
          console.log('Admin check response:', adminResponse);
          
          setCurrentUser({ 
            id: userIdResponse.id, 
            email: email 
          });
          setIsAuthenticated(true);
          setIsAdmin(adminResponse.output || false);
          localStorage.setItem('userId', userIdResponse.id.toString());
          
          return { success: true };
        } else {
          return { success: false, message: 'User not found' };
        }
      } else {
        return { success: false, message: response?.message || 'Invalid credentials' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'An error occurred during login' };
    }
  };

  const signup = async (userData) => {
    try {
      console.log('Attempting signup for:', userData.email);
      
      const response = await volassisAPI.signUp(userData);
      console.log('Signup response:', response);
      
      if (response && response.output) {
        return { success: true, message: 'Account created successfully!' };
      } else {
        return { success: false, message: response?.message || 'Signup failed' };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, message: 'An error occurred during signup' };
    }
  };

  const logout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    localStorage.removeItem('userId');
  };

  const testBackendConnection = async () => {
    try {
      const result = await volassisAPI.testConnection();
      return result;
    } catch (error) {
      console.error('Backend connection test failed:', error);
      return { success: false, message: 'Backend connection test failed', error: error.message };
    }
  };

  const value = {
    currentUser,
    isAuthenticated,
    isAdmin,
    loading,
    login,
    signup,
    logout,
    testBackendConnection,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
